/**
 * ClarifyingQuestionCard - Interactive UI for agent to ask user questions
 * 
 * Design principles:
 * - Clear call-to-action
 * - Multiple choice options when available (one-click answer)
 * - Free-text input when no options
 * - Friendly, conversational tone
 * - Animated entrance for attention
 */

import React, { useState, useRef, useEffect } from 'react';
import type { ClarifyingQuestion } from '../../../api/services/agent';

interface ClarifyingQuestionCardProps {
  question: ClarifyingQuestion;
  onAnswer: (answer: string) => void;
  onDismiss?: () => void;
  isLoading?: boolean;
  className?: string;
}

export const ClarifyingQuestionCard: React.FC<ClarifyingQuestionCardProps> = ({
  question,
  onAnswer,
  onDismiss,
  isLoading = false,
  className = '',
}) => {
  const [textInput, setTextInput] = useState('');
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasOptions = question.options && question.options.length > 0;

  useEffect(() => {
    // Focus input when card mounts (for free-text questions)
    if (!hasOptions && inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasOptions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (textInput.trim()) {
      onAnswer(textInput.trim());
    }
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    // Small delay for visual feedback before submitting
    setTimeout(() => {
      onAnswer(option);
    }, 150);
  };

  return (
    <div
      className={`rounded-xl border-2 border-[color:var(--color-primary-300)] bg-gradient-to-br from-[color:var(--color-primary-25)] to-[color:var(--color-primary-50)] p-5 shadow-sm animate-in slide-in-from-bottom-2 duration-300 ${className}`}
    >
      {/* Header with icon */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          style={{ 
            background: 'linear-gradient(135deg, var(--color-primary-400), var(--color-primary-500))',
          }}
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-[15px] font-semibold text-[color:var(--color-primary-800)]">
            I need your help
          </h4>
          <p className="text-[13px] text-[color:var(--color-primary-600)] mt-0.5">
            {question.context}
          </p>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded-full hover:bg-[color:var(--color-primary-100)] transition-colors"
            aria-label="Dismiss question"
          >
            <svg
              className="w-4 h-4 text-[color:var(--color-primary-400)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Question */}
      <div className="mb-4 p-3 rounded-lg bg-white/70 border border-[color:var(--color-primary-100)]">
        <p className="text-[15px] font-medium text-[color:var(--color-neutral-900)] leading-relaxed">
          {question.question}
        </p>
      </div>

      {/* Options or text input */}
      {hasOptions ? (
        <div className="space-y-2">
          {question.options!.map((option, index) => (
            <button
              key={`option-${index}`}
              type="button"
              onClick={() => handleOptionClick(option)}
              disabled={isLoading}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                selectedOption === option
                  ? 'border-[color:var(--color-primary-500)] bg-[color:var(--color-primary-100)] text-[color:var(--color-primary-800)]'
                  : 'border-[color:var(--color-primary-200)] bg-white hover:border-[color:var(--color-primary-400)] hover:bg-[color:var(--color-primary-50)] text-[color:var(--color-neutral-800)]'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-[12px] font-bold transition-colors ${
                    selectedOption === option
                      ? 'border-[color:var(--color-primary-500)] bg-[color:var(--color-primary-500)] text-white'
                      : 'border-[color:var(--color-primary-300)] text-[color:var(--color-primary-500)]'
                  }`}
                >
                  {String.fromCharCode(65 + index)}
                </div>
                <span className="text-[14px]">{option}</span>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            ref={inputRef}
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Type your answer..."
            disabled={isLoading}
            className="w-full px-4 py-3 rounded-lg border-2 border-[color:var(--color-primary-200)] bg-white text-[14px] text-[color:var(--color-neutral-900)] placeholder-[color:var(--color-neutral-400)] focus:outline-none focus:border-[color:var(--color-primary-500)] focus:ring-2 focus:ring-[color:var(--color-primary-100)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            type="submit"
            disabled={!textInput.trim() || isLoading}
            className="w-full px-4 py-3 rounded-lg font-medium text-[14px] text-white bg-gradient-to-r from-[color:var(--color-primary-500)] to-[color:var(--color-primary-600)] hover:from-[color:var(--color-primary-600)] hover:to-[color:var(--color-primary-700)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-primary-400)] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-[color:var(--color-primary-500)] disabled:hover:to-[color:var(--color-primary-600)]"
          >
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              'Send Answer'
            )}
          </button>
        </form>
      )}

      {/* Skip option */}
      {!hasOptions && (
        <p className="mt-3 text-center text-[12px] text-[color:var(--color-primary-500)]">
          Press Enter to submit, or{' '}
          <button
            type="button"
            onClick={() => onAnswer("I don't know")}
            className="underline hover:text-[color:var(--color-primary-700)]"
          >
            skip this question
          </button>
        </p>
      )}
    </div>
  );
};

/**
 * ClarifyingQuestionsList - Render multiple clarifying questions
 */
interface ClarifyingQuestionsListProps {
  questions: ClarifyingQuestion[];
  onAnswer: (question: ClarifyingQuestion, answer: string) => void;
  isLoading?: boolean;
  className?: string;
}

export const ClarifyingQuestionsList: React.FC<ClarifyingQuestionsListProps> = ({
  questions,
  onAnswer,
  isLoading = false,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!questions || questions.length === 0) {
    return null;
  }

  const currentQuestion = questions[currentIndex];

  const handleAnswer = (answer: string) => {
    onAnswer(currentQuestion, answer);
    // Move to next question if available
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div className={className}>
      {/* Progress indicator */}
      {questions.length > 1 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] text-[color:var(--color-neutral-500)]">
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="flex-1 h-1 rounded-full bg-[color:var(--color-neutral-200)]">
            <div
              className="h-full rounded-full bg-[color:var(--color-primary-500)] transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      <ClarifyingQuestionCard
        question={currentQuestion}
        onAnswer={handleAnswer}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ClarifyingQuestionCard;

